import type { CollapseProps } from 'antd';
import { Collapse, Empty, Flex, List, Skeleton, Typography } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStudyLinesWithSupervisors } from '../../hooks/useStudyLines';
import { useCurrentStudyYear } from '../../hooks/useStudyYear';
import { StudyYearDropdown } from '../StudyYearDropdown';

export const StudyLinesAndSupervisorsCollapse = () => {
  const { t } = useTranslation();
  const { data: studyLinesWithSupervisors, isLoading } =
    useStudyLinesWithSupervisors();
  // undefined until the user selects a study year, then the dropdown shows the current one
  const [selectedStudyYearId, setSelectedStudyYearId] = useState<number>();
  const { data: currentStudyYear } = useCurrentStudyYear();

  if (isLoading) {
    return <Skeleton active paragraph={{ rows: 6 }} />;
  }

  // the dropdown shows the current study year until the user selects another one, the same is filtered here
  const shownStudyYearId = selectedStudyYearId ?? currentStudyYear?.id;

  const shownStudyLines = studyLinesWithSupervisors?.filter(
    (studyLine) => studyLine.studyYearId === shownStudyYearId
  );

  const items: CollapseProps['items'] = shownStudyLines?.map((x) => ({
    key: x.studyLineName,
    label: x.studyLineName,
    children: (
      <List
        size="small"
        dataSource={x.supervisorNames}
        renderItem={(item) => <List.Item>{item}</List.Item>}
      />
    ),
  }));

  const allKeys = shownStudyLines?.map((sl) => sl.studyLineName);

  return (
    <>
      <Flex
        align="center"
        justify="start"
        gap={8}
        wrap
        style={{ marginBottom: 10 }}
      >
        <Typography.Text strong>{t('studyYear.label')}:</Typography.Text>
        <StudyYearDropdown
          value={selectedStudyYearId}
          onChange={setSelectedStudyYearId}
        />
      </Flex>
      {items && items.length > 0 ? (
        // a new study year opens all of its study lines again
        <Collapse
          key={shownStudyYearId}
          items={items}
          defaultActiveKey={allKeys}
        />
      ) : (
        <Empty />
      )}
    </>
  );
};
